/**
 * Number / time / duration formatters shared across every dashboard widget.
 *
 * The contract: every widget renders numbers, deltas, dates, and series
 * names through these helpers so the same value never looks different on
 * two widgets in the same dashboard. Inspired by Grafana's
 * `valueFormats` registry and Datadog's metric formatters.
 *
 * All helpers are pure and locale-aware. They accept anything-shaped
 * inputs (string, number, null, undefined) and gracefully degrade to a
 * stable placeholder ("—") rather than throwing — widgets render what
 * Postgres handed back, so this layer has to be defensive.
 */

export type ValueFormat =
  | "number"
  | "compact"
  | "percent"
  | "percent_fraction"
  | "currency"
  | "duration_ms"
  | "duration_seconds"
  | "bytes"
  | "auto";

export interface FormatOptions {
  format?: ValueFormat;
  /** Force this many fraction digits regardless of magnitude. */
  digits?: number;
  /** Currency code for `currency` format. Defaults to USD. */
  currency?: string;
  /** Locale override (defaults to runtime locale). */
  locale?: string;
}

const PLACEHOLDER = "—";

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Generic number formatter. Picks fraction digits from the value's
 * magnitude (1.234 → "1.23", 12.3 → "12.3", 1234 → "1,234").
 */
export function formatNumber(value: unknown, options: FormatOptions = {}): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return PLACEHOLDER;
  const { digits, locale } = options;
  if (digits !== undefined) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(numeric);
  }
  const abs = Math.abs(numeric);
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: abs < 1 ? 3 : abs < 10 ? 2 : abs < 100 ? 1 : 0,
  }).format(numeric);
}

/**
 * Compact (k / M / B / T) number formatter. Falls back to a regular
 * locale number for absolute values below 1,000.
 */
export function formatCompact(value: unknown, options: FormatOptions = {}): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return PLACEHOLDER;
  const abs = Math.abs(numeric);
  if (abs < 1000) return formatNumber(numeric, options);
  // `Intl.NumberFormat` with `notation: "compact"` matches Grafana's
  // short-notation behavior and handles localization (e.g. "1.2k" vs
  // "1,2k" in fr-FR) for free.
  return new Intl.NumberFormat(options.locale, {
    notation: "compact",
    maximumFractionDigits: abs < 10_000 ? 1 : 0,
  }).format(numeric);
}

/**
 * Format an already-percentage value (5 → "5%"). Used for
 * `format: "percent"` widget configs.
 */
export function formatPercent(value: unknown, options: FormatOptions = {}): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return PLACEHOLDER;
  const digits = options.digits ?? (Math.abs(numeric) < 1 ? 2 : Math.abs(numeric) < 10 ? 1 : 0);
  return `${numeric.toFixed(digits)}%`;
}

/**
 * Format a fraction value (0.05 → "5%"). Use `percent_fraction` for
 * SQL that returns rates as fractions instead of pre-multiplied
 * percentages.
 */
export function formatPercentFromFraction(
  value: unknown,
  options: FormatOptions = {},
): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return PLACEHOLDER;
  return formatPercent(numeric * 100, options);
}

/**
 * Format a currency value. Defaults to USD with no fractional digits
 * (most ops dashboards don't need cents); pass `digits: 2` to get them.
 */
export function formatCurrency(value: unknown, options: FormatOptions = {}): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return PLACEHOLDER;
  return new Intl.NumberFormat(options.locale, {
    style: "currency",
    currency: options.currency ?? "USD",
    maximumFractionDigits: options.digits ?? 0,
  }).format(numeric);
}

/**
 * Human-readable duration. Accepts either a millisecond count
 * (`format: "duration_ms"`) or a second count (`format:
 * "duration_seconds"`). Output: "12 ms", "3.4 s", "2 m 3 s", "1 h 5
 * m", "2 d 4 h".
 */
export function formatDurationMs(value: unknown): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return PLACEHOLDER;
  if (numeric < 1) return `${numeric.toFixed(2)} ms`;
  if (numeric < 1000) return `${numeric.toFixed(0)} ms`;
  return formatDurationSeconds(numeric / 1000);
}

export function formatDurationSeconds(value: unknown): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return PLACEHOLDER;
  // Format magnitude and reapply sign at the end. This avoids two
  // bugs the natural floor/round implementation runs into:
  //   * negative durations: `Math.floor(-65 / 60)` is -2, leaving the
  //     remainder as positive 55, so we'd render "-2 m 55 s" instead
  //     of "-1 m 5 s".
  //   * remainder rollover: rounding 59.6 seconds gives 60, producing
  //     "1 h 60 m" or "5 m 60 s". Cap each remainder at one less than
  //     the next unit's boundary.
  const sign = numeric < 0 ? "-" : "";
  const abs = Math.abs(numeric);
  if (abs < 60) {
    return abs < 10 ? `${sign}${abs.toFixed(1)} s` : `${sign}${abs.toFixed(0)} s`;
  }
  if (abs < 3600) {
    const minutes = Math.floor(abs / 60);
    const seconds = Math.min(59, Math.round(abs - minutes * 60));
    return seconds === 0 ? `${sign}${minutes} m` : `${sign}${minutes} m ${seconds} s`;
  }
  if (abs < 86_400) {
    const hours = Math.floor(abs / 3600);
    const minutes = Math.min(59, Math.round((abs - hours * 3600) / 60));
    return minutes === 0 ? `${sign}${hours} h` : `${sign}${hours} h ${minutes} m`;
  }
  const days = Math.floor(abs / 86_400);
  const hours = Math.min(23, Math.round((abs - days * 86_400) / 3600));
  return hours === 0 ? `${sign}${days} d` : `${sign}${days} d ${hours} h`;
}

/**
 * Bytes → human-readable (KB / MB / GB / TB / PB). Uses base-1024 to
 * match what most Postgres / OS-level metrics return.
 */
export function formatBytes(value: unknown): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return PLACEHOLDER;
  const sign = numeric < 0 ? "-" : "";
  let abs = Math.abs(numeric);
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let unitIndex = 0;
  while (abs >= 1024 && unitIndex < units.length - 1) {
    abs /= 1024;
    unitIndex += 1;
  }
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return `${sign}${abs.toFixed(digits)} ${units[unitIndex]}`;
}

/**
 * Auto-detect format from the value when no explicit format was given.
 * The heuristic intentionally errs on the side of `compact` for large
 * counts — production dashboards almost never want a raw "1,247,392"
 * cell where "1.2M" reads better.
 */
export function formatAuto(value: unknown, options: FormatOptions = {}): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return PLACEHOLDER;
  if (Math.abs(numeric) >= 10_000) return formatCompact(numeric, options);
  return formatNumber(numeric, options);
}

/**
 * Single dispatcher that matches the `format` enum every widget config
 * uses. Any new ValueFormat added to the union must be wired here so
 * widgets get coherent behavior automatically.
 */
export function formatValue(value: unknown, options: FormatOptions = {}): string {
  switch (options.format) {
    case "compact":
      return formatCompact(value, options);
    case "percent":
      return formatPercent(value, options);
    case "percent_fraction":
      return formatPercentFromFraction(value, options);
    case "currency":
      return formatCurrency(value, options);
    case "duration_ms":
      return formatDurationMs(value);
    case "duration_seconds":
      return formatDurationSeconds(value);
    case "bytes":
      return formatBytes(value);
    case "number":
      return formatNumber(value, options);
    case "auto":
      return formatAuto(value, options);
    case undefined:
      return formatNumber(value, options);
    default: {
      const _exhaustive: never = options.format;
      return _exhaustive;
    }
  }
}

// ---------------------------------------------------------------------------
// Time helpers
// ---------------------------------------------------------------------------

const ISO_PREFIX = /^\d{4}-\d{2}-\d{2}/;

export function isLikelyTimestamp(value: unknown): boolean {
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  if (typeof value !== "string") return false;
  if (!ISO_PREFIX.test(value)) return false;
  const parsed = Date.parse(value);
  return !Number.isNaN(parsed);
}

function asDate(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number" && Number.isFinite(value)) return new Date(value);
  if (typeof value === "string" && isLikelyTimestamp(value)) return new Date(value);
  return null;
}

/**
 * Long-form timestamp ("Apr 28, 2026, 5:33 PM"). Used for widget
 * tooltips and the dashboard "updated at" header.
 */
export function formatTimestamp(value: unknown, locale?: string): string {
  const date = asDate(value);
  if (!date) return value === null || value === undefined ? PLACEHOLDER : String(value);
  return date.toLocaleString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Compact axis-style timestamp formatter. Picks granularity from the
 * value: bare dates render as "Apr 28", timestamps as "Apr 28 14:32".
 */
export function formatTickTimestamp(value: unknown, locale?: string): string {
  const date = asDate(value);
  if (!date) return value === null || value === undefined ? "" : String(value);
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
  }
  return date.toLocaleString(locale, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Auto-format a tick label. Picks compact-numeric for numbers, ISO
 * timestamp formatting for date-shaped strings, otherwise just `String`.
 */
export function formatAxisTick(value: unknown, options: FormatOptions = {}): string {
  if (typeof value === "number") {
    return formatCompact(value, options);
  }
  if (isLikelyTimestamp(value)) {
    return formatTickTimestamp(value, options.locale);
  }
  return value === null || value === undefined ? "" : String(value);
}

/**
 * Compute the change between `current` and `previous` and return a
 * structured delta record callers can render directly. Returns null
 * when either value is missing so callers can decide between "no
 * comparison available" and "no change."
 */
export interface DeltaResult {
  /** Absolute change (`current - previous`). */
  absolute: number;
  /** Relative change as a fraction; `null` if `previous` is zero. */
  fraction: number | null;
  /** Sign: -1, 0, or +1. */
  direction: -1 | 0 | 1;
}

export function computeDelta(current: unknown, previous: unknown): DeltaResult | null {
  const a = toFiniteNumber(current);
  const b = toFiniteNumber(previous);
  if (a === null || b === null) return null;
  const absolute = a - b;
  const fraction = b === 0 ? null : absolute / Math.abs(b);
  const direction = absolute === 0 ? 0 : absolute > 0 ? 1 : -1;
  return { absolute, fraction, direction };
}
